/**
 * This team's season stats, collapsed into three states the Stats panel can
 * render without branching on query internals.
 *
 * A FAILED CALL IS `absent`, NOT AN ERROR, and that is the whole design. A
 * league with no table, a season that has not started, and a transport failure
 * all mean the same thing to a fan who opened this screen to look at the
 * schedule, which is that this panel has nothing to print. None of them is
 * worth a page level error over a screen that otherwise works, so this hook
 * deliberately does not call `showApiError` the way `useTeamProfileData` does
 * for the two reads the screen cannot render without.
 *
 * THE TAB KEY IS NOT DECIDED HERE. ISSUE-856 moved that to sports-api, which
 * settles `stats` per team from the standings row it already reads. This hook
 * loads only what the panel PRINTS. `absent` means there is nothing to draw; it
 * never means there is no tab.
 *
 * `resolveTeamStatsState` is pure and is tested directly. The hook itself is
 * covered through the mounted panel in `TeamStatsTab.test.tsx`.
 */
import { useStandingsGet } from "@sportsmet/sports-sdk/private";

import type { typeStandingsDataDto } from "../standings/standingsTypes";
import { toTeamStatsView, type typeTeamStatsView } from "./teamStatsLogic";

/**
 * `ready` always carries at least one printed statistic, because
 * `toTeamStatsView` returns null rather than an empty view.
 */
export type typeTeamStatsState =
  | { kind: "absent" }
  | { kind: "loading" }
  | { kind: "ready"; view: typeTeamStatsView };

export function resolveTeamStatsState(input: {
  teamId: string;
  leagueId: string | null;
  isPending: boolean;
  isError: boolean;
  errorCode: string | null;
  data: typeStandingsDataDto | null;
}): typeTeamStatsState {
  if (input.teamId.length === 0) {
    return { kind: "absent" };
  }

  // BEFORE the isPending check, and that order is load bearing: react-query
  // reports isPending forever on a disabled query, so testing pending first
  // would leave a team with no league spinning a loading panel for good.
  if (input.leagueId === null || input.leagueId.length === 0) {
    return { kind: "absent" };
  }

  // A table we already hold beats a failed or in flight refetch, which is why
  // this sits ABOVE both the error check and the pending check. react-query
  // keeps `data` alongside `isError` when a BACKGROUND refetch fails, so
  // testing the error first would blank a panel a fan is currently reading.
  const view =
    input.data === null ? null : toTeamStatsView(input.data, input.teamId);
  if (view !== null) {
    return { kind: "ready", view };
  }

  if (input.isError || input.errorCode !== null) {
    return { kind: "absent" };
  }

  if (input.isPending) {
    return { kind: "loading" };
  }

  return { kind: "absent" };
}

export function useTeamStats(input: {
  teamId: string;
  leagueId: string | null;
}): typeTeamStatsState {
  const isEnabled =
    input.teamId.length > 0 &&
    input.leagueId !== null &&
    input.leagueId.length > 0;

  // Called unconditionally at the top level; only `enabled` varies. Wrapping a
  // hook in a condition is how a render order changes between two renders.
  //
  // `grouping: null` asks for the season's own default grouping rather than
  // naming one here. Which grouping a league ranks by is the season's decision,
  // and a client that picked one would report a conference rank for a league
  // that ranks by division.
  //
  // The same 60 second staleTime `StandingsScreen` uses, which spares a repeat
  // fetch when a fan moves between two teams in the same league. It does NOT
  // share a cache entry with that screen: the grouping is part of the query
  // key, and the standings screen writes the server resolved grouping back into
  // its own route params, so its key reads a real grouping while this one
  // always reads null.
  //
  // Reading a whole league table to print about five numbers is a known cost,
  // not an accident. It is paid only when a fan opens this tab, because
  // `TeamStatsTab` is unmounted until then. The real fix is a per team season
  // stats payload on POST /team/profile-get, which removes this call outright;
  // that is a schema change and a codegen run, and it is its own card.
  // `leagueId` goes through as it is, null included, because the generated body
  // type is `string | null`. An earlier revision sent `?? ""` for the null case,
  // which was a fiction: it claimed a league id this app does not have, and it
  // leaned on the payload validator rejecting the empty string to stop a call
  // `enabled` should be stopping. `enabled` is the guard.
  const query = useStandingsGet(
    { leagueId: input.leagueId, grouping: null },
    { enabled: isEnabled, staleTime: 60_000 },
  );

  return resolveTeamStatsState({
    teamId: input.teamId,
    leagueId: input.leagueId,
    isPending: query.isPending,
    isError: query.isError,
    errorCode: query.data?.error?.code ?? null,
    data: query.data?.data ?? null,
  });
}
